import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { FileText, Plus, Trash2, Loader2, Link as LinkIcon, Upload, X } from "lucide-react";

import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form";
import { Textarea } from "@/shared/ui/textarea";
import { Badge } from "@/shared/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog";

import {
  PaymentDocument,
  PaymentDocumentType,
  PAYMENT_DOCUMENT_TYPE_LABELS,
  PendingPaymentDocument,
} from "../utils/types";
import {
  usePaymentDocuments,
  useCreatePaymentDocument,
  useDeletePaymentDocument,
} from "../hooks";
import { useTranslation } from "@/shared/i18n";

const documentFormSchema = z
  .object({
    documentType: z.nativeEnum(PaymentDocumentType, {
      required_error: "Selecione o tipo do documento",
    }),
    description: z
      .string()
      .max(255, "Máximo de 255 caracteres")
      .optional(),
    file: z.instanceof(File).optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.file) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Selecione um arquivo",
        path: ["file"],
      });
    }
  });

type DocumentFormValues = Omit<z.infer<typeof documentFormSchema>, "file"> & { file?: File };

interface PaymentDocumentsSectionProps {
  paymentId?: string;
  pendingDocuments?: PendingPaymentDocument[];
  onPendingDocumentCreate?: (document: PendingPaymentDocument) => void;
  onPendingDocumentDelete?: (id: string) => void;
  isProcessing?: boolean;
  isLocked?: boolean;
}

type DocumentListItem =
  | { kind: "existing"; document: PaymentDocument }
  | { kind: "pending"; document: PendingPaymentDocument };

const generateTempId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export function PaymentDocumentsSection({
  paymentId,
  pendingDocuments = [],
  onPendingDocumentCreate,
  onPendingDocumentDelete,
  isProcessing = false,
  isLocked = false,
}: PaymentDocumentsSectionProps) {
  const { t } = useTranslation('payments-components');
  const {
    documents,
    isLoading: loadingDocuments,
    isFetching: fetchingDocuments,
    refetch: refetchDocuments,
  } = usePaymentDocuments(paymentId);
  const { createPaymentDocument, isLoading: creatingDocument } = useCreatePaymentDocument();
  const { deletePaymentDocument, isLoading: deletingDocument } = useDeletePaymentDocument();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<DocumentListItem | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const form = useForm<DocumentFormValues>({
    resolver: zodResolver(documentFormSchema),
    defaultValues: {
      documentType: PaymentDocumentType.WIRE_RECEIPT,
      description: "",
      file: undefined,
    },
  });

  const selectedFile = form.watch("file");
  const hasPaymentId = Boolean(paymentId);

  const documentTypeOptions = useMemo(
    () => Object.values(PaymentDocumentType),
    [],
  );

  const sortedExistingDocuments = useMemo(() => {
    return [...documents].sort((a, b) => {
      const aDate = new Date(a.createdAt).getTime();
      const bDate = new Date(b.createdAt).getTime();
      return bDate - aDate;
    });
  }, [documents]);

  const sortedPendingDocuments = useMemo(() => {
    return [...pendingDocuments].sort((a, b) => {
      const aDate = new Date(a.createdAt).getTime();
      const bDate = new Date(b.createdAt).getTime();
      return bDate - aDate;
    });
  }, [pendingDocuments]);

  const listItems: DocumentListItem[] = hasPaymentId
    ? sortedExistingDocuments.map((document) => ({ kind: "existing", document }))
    : sortedPendingDocuments.map((document) => ({ kind: "pending", document }));

  useEffect(() => {
    if (selectedFile instanceof File) {
      form.clearErrors("file");
    }
  }, [selectedFile, form]);

  const resetFormState = (preserveType?: PaymentDocumentType) => {
    form.reset({
      documentType: preserveType ?? PaymentDocumentType.WIRE_RECEIPT,
      description: "",
      file: undefined,
    });
    setFileInputKey((prev) => prev + 1);
  };

  const formatDate = (value?: string) => {
    if (!value) return "—";
    try {
      return format(new Date(value), "dd/MM/yyyy HH:mm", { locale: ptBR });
    } catch {
      return value;
    }
  };

  const handleCreateSubmit = async (values: DocumentFormValues) => {
    if (isLocked) return;
    if (isProcessing) return;
    const description = values.description?.trim() ? values.description.trim() : undefined;
    const file = values.file instanceof File ? values.file : undefined;

    if (!file) {
      form.setError("file", {
        type: "manual",
        message: t('fileRequired'),
      });
      return;
    }

    if (hasPaymentId && paymentId) {
      try {
        await createPaymentDocument({
          paymentId,
          documentType: values.documentType,
          description,
          file,
        });
        // Apenas em caso de sucesso: fechar o modal e atualizar a lista de documentos
        resetFormState(values.documentType);
        setIsCreateDialogOpen(false);
        void refetchDocuments();
      } catch (error) {
        // Em caso de erro: manter o modal aberto para o usuário ver o erro
        // O toast de erro já é exibido pelo hook useCreatePaymentDocument
        // Não fechar o modal para que o usuário possa tentar novamente
        console.error("Erro ao criar documento:", error);
        // Não fazer nada aqui, o modal permanece aberto
      }
      return;
    }

    const pendingDocument: PendingPaymentDocument = {
      id: generateTempId(),
      documentType: values.documentType,
      description,
      file,
      createdAt: new Date().toISOString(),
    };
    onPendingDocumentCreate?.(pendingDocument);
    resetFormState(values.documentType);
    setIsCreateDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteItem) return;

    if (deleteItem.kind === "existing") {
      if (!paymentId) return;
      await deletePaymentDocument({ id: deleteItem.document.id, paymentId });
      setDeleteItem(null);
      refetchDocuments();
      return;
    }

    onPendingDocumentDelete?.(deleteItem.document.id);
    setDeleteItem(null);
  };

  return (
    <Card className="bg-white/[0.03] border-white/[0.06]">
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{t('documents')}</CardTitle>
        <CardDescription>
          {t('documentsDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t('documents')}</h3>
            <p className="text-xs text-muted-foreground">
              {t('newDocumentDescription')}
            </p>
            {isLocked && (
              <p className="text-xs font-medium text-amber-500">
                {t('completedReceivablesMessage')}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {hasPaymentId && (loadingDocuments || fetchingDocuments) && (
              <span className="flex items-center text-xs text-muted-foreground">
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                {t('updating')}
              </span>
            )}
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (isLocked) return;
                setIsCreateDialogOpen(true);
              }}
              disabled={isProcessing || creatingDocument || isLocked}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('newDocument')}
            </Button>
          </div>
        </div>

        {hasPaymentId && loadingDocuments ? (
          <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('loadingDocuments')}
          </div>
        ) : listItems.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
            {t('noDocuments')}
          </div>
        ) : (
          <ul className="space-y-2">
            {listItems.map((item) => {
              const document = item.document;
              const createdLabel = formatDate(document.createdAt);
              const typeLabel =
                PAYMENT_DOCUMENT_TYPE_LABELS[
                (document.documentType as PaymentDocumentType) ?? PaymentDocumentType.WIRE_RECEIPT
                ] ?? document.documentType;
              return (
                <li key={`${item.kind}-${document.id}`} className="rounded-md border bg-muted/30 p-4">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex flex-1 gap-3">
                      <div className="mt-1 text-muted-foreground">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="secondary">{typeLabel}</Badge>
                          <span className="text-xs text-muted-foreground">Adicionado em {createdLabel}</span>
                          {item.kind === "pending" && (
                            <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                              Pendente
                            </Badge>
                          )}
                        </div>
                        {document.description ? (
                          <p className="break-words text-sm text-muted-foreground">{document.description}</p>
                        ) : (
                          <p className="text-sm italic text-muted-foreground">Sem descrição</p>
                        )}
                        {item.kind === "existing" && "documentUrl" in document && document.documentUrl && (
                          <a
                            href={document.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                          >
                            <LinkIcon className="h-4 w-4" />
                            Abrir documento
                          </a>
                        )}
                        {item.kind === "pending" && "file" in document && document.file && (
                          <span className="text-xs text-muted-foreground">Arquivo: {document.file.name}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteItem(item)}
                      disabled={isProcessing || creatingDocument || deletingDocument}
                      title="Remover documento"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <Dialog
        open={isCreateDialogOpen}
        onOpenChange={(open) => {
          // Prevenir fechamento durante criação de documento
          if (creatingDocument) return;
          if (open && isLocked) return;
          if (!open) {
            resetFormState(form.getValues("documentType"));
          }
          setIsCreateDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Adicionar documento</DialogTitle>
            <DialogDescription>
              Anexe um arquivo relacionado ao pagamento. Você pode adicionar comprovantes, notas fiscais ou outros documentos relevantes.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void form.handleSubmit(handleCreateSubmit)(e);
              }}
              className="space-y-6"
            >
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="documentType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold">Tipo do documento</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Selecione o tipo de documento" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {documentTypeOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {PAYMENT_DOCUMENT_TYPE_LABELS[option] ?? option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1.5">
                        Selecione a categoria que melhor descreve este documento.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="file"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold">Arquivo</FormLabel>
                      <input
                        key={fileInputKey}
                        ref={(node) => (fileInputRef.current = node)}
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          field.onChange(file ?? undefined);
                        }}
                        className="hidden"
                      />
                      {field.value ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                              <FileText className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate" title={field.value.name}>
                                {field.value.name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {(field.value.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => {
                                field.onChange(undefined);
                                setFileInputKey((prev) => prev + 1);
                              }}
                              title="Remover arquivo"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            className="w-full"
                          >
                            <Upload className="mr-2 h-4 w-4" />
                            Trocar arquivo
                          </Button>
                        </div>
                      ) : (
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="group relative cursor-pointer rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted/30 p-8 transition-colors hover:border-primary/50 hover:bg-muted/50"
                        >
                          <div className="flex flex-col items-center justify-center gap-3 text-center">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                              <Upload className="h-6 w-6 text-primary" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">
                                Clique para selecionar ou arraste o arquivo aqui
                              </p>
                              <p className="mt-1 text-xs text-muted-foreground">
                                Formatos aceitos: PDF, JPG, PNG, DOC, DOCX (máx. 10 MB)
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base font-semibold">Descrição (opcional)</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Adicione uma descrição ou observação sobre este documento..."
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          className="min-h-[100px] resize-none"
                          rows={4}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground mt-1.5">
                        Informações adicionais que ajudem a identificar ou contextualizar este documento.
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateDialogOpen(false)}
                  disabled={isProcessing || creatingDocument}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={isProcessing || creatingDocument}
                  className="min-w-[120px]"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {creatingDocument ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Plus className="mr-2 h-4 w-4" />
                      Adicionar documento
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteItem)} onOpenChange={(open) => !open && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover documento?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação não pode ser desfeita. O documento será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingDocument || isProcessing}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} disabled={deletingDocument || isProcessing}>
              {deletingDocument ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removendo...
                </>
              ) : (
                "Remover"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
